// src/utils/documentationEDE.ts — SCE Issues
// Section Documentation (Lot 13) : le schéma électrique (EDE) d'un problème
// ELEC / AUTOM — format, saisie, révision la plus récente. Pur, testable.
//
// ─── Le format ───
//
//   NVS50312-G-EDE Rev 01
//   └──────┬─┘      └┬┘
//     numéro        révision (2 chiffres)
//
// Le numéro (`NVS` + 5 chiffres + `-` + une lettre) désigne le document. Ses
// chiffres sont ceux de la FOURNITURE Windchill de l'équipement (« LP450 cust
// NVS50312 » → EDE NVS50312-G) : les équipements de même NVS partagent donc le
// même schéma (précision de Guillaume du 26/09). Sans NVS dans la fourniture,
// l'EDE reste propre au N° de série. Absent de Windchill : il est saisi à la
// main, après coup, par le coordinateur. Les page(s) sont propres au
// PROBLÈME : `12`, `12-14`, `12, 18`.
//
// ─── La révision (décision du 25/09) ───
//
// Chaque problème CONSERVE la révision sous laquelle il a été renseigné, et
// PRÉCISE la plus récente connue pour son équipement (ou son NVS) : déclaré en
// Rev 01, il reste en Rev 01 ; quand un autre problème du même EDE est renseigné
// en Rev 02, le premier affiche « Rev 01 — plus récente connue : Rev 02 ».
// Un problème sans EDE REPREND l'EDE et la dernière révision de son
// équipement — à l'affichage et à l'export, sans rien écrire.
//
// Colonnes (SCE_Problemes) : NumeroEDE, RevEDE, PageEDE — une ligne de texte
// chacune.

import { nvsDeFourniture } from '@/config/equipements.catalogue'
import {
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  normaliserNumeroPlan,
  suffixeNumeroSerie,
  type Equipement,
  type Issue,
  type Metier,
} from '@/types/issue.types'

/** Le schéma électrique ne concerne que ces métiers. */
export const METIERS_EDE: readonly Metier[] = ['ELEC', 'AUTOM']

export function concerneEDE(metier: Metier): boolean {
  return METIERS_EDE.includes(metier)
}

// ─── Saisie ───

const MOTIF_EDE =
  /^\s*NVS\s*(\d{5})\s*-?\s*([A-Za-z])\s*(?:-?\s*EDE)?\s*(?:[-,]?\s*R[EÉ]V(?:ISION)?\.?\s*(\d{1,2}))?\s*$/i

export interface SaisieEDE {
  /** Tel que tapé : numéro seul, ou référence complète avec la révision. */
  numero: string
  /** Champ révision ; ignoré si la référence complète en contient une. */
  rev: string
  pages: string
}

export interface DocumentationEDE {
  numeroEDE: string
  revEDE: string
  pageEDE: string
}

/**
 * Lit la saisie du coordinateur et la met au format. Tolère la casse, les
 * espaces et la référence complète collée depuis un document
 * (« nvs 50312-g-ede rev 1 » → NVS50312-G, 01).
 *
 * Tout vide = retirer l'EDE du problème.
 * @throws Error avec un message affichable si la saisie est incomplète ou
 *         hors format — rien ne doit partir en l'état.
 */
export function normaliserSaisieEDE(saisie: SaisieEDE): DocumentationEDE {
  const brut = saisie.numero.trim()
  const pagesBrutes = saisie.pages.trim()
  const revBrute = saisie.rev.trim()
  if (!brut && !revBrute && !pagesBrutes) return { numeroEDE: '', revEDE: '', pageEDE: '' }
  if (!brut) throw new Error('Indiquez le N° de l’EDE (ex. NVS50312-G).')

  const m = MOTIF_EDE.exec(brut)
  if (!m) throw new Error('N° d’EDE attendu au format NVS50312-G (NVS, 5 chiffres, une lettre).')
  const numeroEDE = `NVS${m[1]}-${(m[2] ?? '').toUpperCase()}`

  const rev = m[3] ?? revBrute
  if (!rev) throw new Error('Indiquez la révision de l’EDE (ex. 01).')
  if (!/^\d{1,2}$/.test(rev)) throw new Error('Révision attendue sur deux chiffres (ex. 01).')

  return { numeroEDE, revEDE: rev.padStart(2, '0'), pageEDE: normaliserPages(pagesBrutes) }
}

/**
 * « 12 - 14 ; 18 » → « 12-14, 18 ». Vide accepté (page inconnue).
 * @throws Error si ce ne sont pas des numéros ou des plages de pages.
 */
export function normaliserPages(texte: string): string {
  if (!texte.trim()) return ''
  const morceaux = texte.split(/[,;]/).map((m) => m.trim()).filter(Boolean)
  const propres = morceaux.map((m) => {
    const plage = /^(\d{1,4})\s*[-–à]\s*(\d{1,4})$/.exec(m)
    if (plage) return `${Number(plage[1])}-${Number(plage[2])}`
    if (/^\d{1,4}$/.test(m)) return String(Number(m))
    throw new Error('Page(s) attendue(s) : un numéro, une plage ou une liste — ex. 12, 12-14, 12, 18.')
  })
  return propres.join(', ')
}

// ─── À la déclaration (15/10) ───

/** Ce que l'opérateur a tapé dans les champs facultatifs du formulaire /new. */
export interface SaisieDocumentation {
  plan: string
  ede: SaisieEDE
}

export const DOCUMENTATION_VIDE: SaisieDocumentation = {
  plan: '',
  ede: { numero: '', rev: '', pages: '' },
}

/**
 * Ce qui s'écrit avec le problème, selon le MÉTIER choisi : le N° de plan d'un
 * MECA, l'EDE d'un ELEC / AUTOM — rien pour les autres, et rien de ce qui a été
 * tapé pour un autre métier avant un changement d'avis. L'EDE déjà connue de la
 * machine (`edeConnue`) n'est pas redemandée : on ne l'écrit pas, la fiche la
 * reprend (cf. `edeAffichee`).
 * @throws Error (message affichable) si l'EDE saisie est hors format.
 */
export function documentationDeclaree(
  metier: Metier,
  saisie: SaisieDocumentation,
  edeConnue: boolean,
): { numeroPlan?: string; numeroEDE?: string; revEDE?: string; pageEDE?: string } {
  if (metier === 'MECA') {
    const numeroPlan = normaliserNumeroPlan(saisie.plan)
    return numeroPlan ? { numeroPlan } : {}
  }
  if (concerneEDE(metier) && !edeConnue) {
    const doc = normaliserSaisieEDE(saisie.ede)
    return doc.numeroEDE ? doc : {}
  }
  return {}
}

// ─── Lecture ───

/** « NVS50312-G-EDE Rev 01 » */
export function libelleEDE(numero: string, rev: string): string {
  return rev ? `${numero}-EDE Rev ${rev}` : `${numero}-EDE`
}

export interface ReferenceEDE {
  numero: string
  rev: string
}

/**
 * Ce qui fait que deux problèmes partagent le même EDE : la clé de leur
 * équipement. Par défaut le N° de série ; avec le référentiel, le NVS.
 */
export type CleEDE = (numeroSerie: string) => string

export const cleParNumeroSerie: CleEDE = (numeroSerie) => cleRapprochement(numeroSerie)

/**
 * Clé de partage lue dans le référentiel : le NVS de la fourniture
 * (« NVS50312 »), sinon le N° de série. Les deux formes ne peuvent pas se
 * confondre (un N° de série porte un tiret et un suffixe).
 */
export function cleEDEDepuisReferentiel(equipements: readonly Equipement[]): CleEDE {
  const nvsParNs = new Map<string, string>()
  for (const eq of equipements) {
    const categorie = CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(eq.numeroSerie)] ?? null
    const nvs = nvsDeFourniture(eq.fourniture, categorie)
    if (nvs) nvsParNs.set(cleRapprochement(eq.numeroSerie), nvs)
  }
  return (numeroSerie) => {
    const cle = cleRapprochement(numeroSerie)
    return nvsParNs.get(cle) ?? cle
  }
}

/**
 * La référence la plus récente connue par équipement : pour chaque clé
 * (N° de série, ou NVS partagé), le numéro et la plus haute révision
 * renseignés sur les problèmes concernés.
 */
export function referencesParEquipement(
  issues: readonly Issue[],
  cleEDE: CleEDE = cleParNumeroSerie,
): Map<string, ReferenceEDE> {
  const refs = new Map<string, ReferenceEDE>()
  for (const i of issues) {
    if (!i.numeroEDE || !i.revEDE) continue
    const cle = cleEDE(i.numeroSerie)
    if (!cle) continue
    const actuelle = refs.get(cle)
    if (!actuelle || Number(i.revEDE) > Number(actuelle.rev)) {
      refs.set(cle, { numero: i.numeroEDE, rev: i.revEDE })
    }
  }
  return refs
}

export interface EDEAffichee {
  numero: string
  /** Révision retenue pour ce problème (la sienne, ou celle reprise). */
  rev: string
  /** Présente si une révision plus récente est connue pour l'équipement. */
  revPlusRecente?: string
  /** Vrai si le problème n'a pas d'EDE à lui : celle de l'équipement (ou de son NVS). */
  reprise: boolean
}

/** Ce que la fiche et l'export montrent pour un problème ; null s'il n'y a rien. */
export function edeAffichee(
  issue: Issue,
  refs: ReadonlyMap<string, ReferenceEDE>,
  cleEDE: CleEDE = cleParNumeroSerie,
): EDEAffichee | null {
  const ref = refs.get(cleEDE(issue.numeroSerie))
  if (issue.numeroEDE) {
    const rev = issue.revEDE ?? ''
    const plusRecente = ref && ref.numero === issue.numeroEDE && Number(ref.rev) > Number(rev) ? ref.rev : undefined
    return { numero: issue.numeroEDE, rev, reprise: false, ...(plusRecente ? { revPlusRecente: plusRecente } : {}) }
  }
  if (!concerneEDE(issue.metier) || !ref) return null
  return { numero: ref.numero, rev: ref.rev, reprise: true }
}

/** Texte de l'événement « Modification » (activité de la fiche). */
export function resumerChangementEDE(avant: DocumentationEDE, apres: DocumentationEDE): string {
  const lib = (d: DocumentationEDE): string =>
    d.numeroEDE ? `${libelleEDE(d.numeroEDE, d.revEDE)}${d.pageEDE ? `, p. ${d.pageEDE}` : ''}` : ''
  const a = lib(avant)
  const b = lib(apres)
  if (!b) return `EDE retiré (était ${a})`
  if (!a) return `EDE renseigné : ${b}`
  return `EDE corrigé : ${a} → ${b}`
}
